import React, { useEffect, useState } from "react";
import { RotateCcw, ChevronUp, ChevronDown, CheckCircle2, XCircle, Info } from "lucide-react";
import baseapi from "../../../services/baseapi";
import { showError } from "../../../utils/toast";

interface CameraUserData {
    id: number;
    username: string;
    user_level: string;
    auth_success: boolean | null;
    created: string;
}

const CameraUsers: React.FC<{ hideHeader?: boolean; onDataLoaded?: (count: number) => void }> = ({ onDataLoaded }) => {
    const [data, setData] = useState<CameraUserData[]>([]);
    const [loading, setLoading] = useState(true);
    const [totalItems, setTotalItems] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(5);
    const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' } | null>(null);

    const info = JSON.parse(localStorage.getItem("internal_ids") || "{}");
    const { asset_id, client_id } = info;

    const fetchData = async () => {
        if (!asset_id) return;
        setLoading(true);
        try {
            const skip = (currentPage - 1) * itemsPerPage;
            const params: any = {
                query: JSON.stringify({ asset_id, client_id }),
                skip,
                limit: itemsPerPage,
            };
            if (sortConfig) {
                params.sort_by = sortConfig.key;
                params.sort_order = sortConfig.direction;
            }
            const response = await baseapi.get("/asset_camera_users", { params });
            if (response.data.status) {
                const total = response.data.total || 0;
                setData(response.data.data || []);
                setTotalItems(total);
                onDataLoaded?.(total);
            }
        } catch (error: any) {
            showError(error.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchData(); }, [currentPage, sortConfig]);

    const formatDate = (dateString: string) => {
        if (!dateString || dateString === "-") return "-";
        try {
            const normalized = /[zZ]|[+-]\d{2}:\d{2}$/.test(dateString) ? dateString : `${dateString}Z`;
            const date = new Date(normalized);
            if (isNaN(date.getTime())) return dateString;
            return date.toLocaleString("en-IN", {
                year: "numeric", month: "short", day: "numeric",
                hour: "2-digit", minute: "2-digit", hour12: true,
            });
        } catch { return dateString; }
    };

    const handleSort = (key: string) => {
        setSortConfig((prev) => (prev?.key === key ? { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' } : { key, direction: 'asc' }));
        setCurrentPage(1);
    };

    const totalPages = Math.ceil(totalItems / itemsPerPage);

    return (
        <div className="space-y-3 animate-fade-in">
            <div className={`bg-white border border-slate-200 overflow-hidden shadow-sm rounded-lg`}>
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-slate-50 border-b border-slate-200">
                                <th className="px-2 py-1.5 text-[10px] font-black text-slate-500 uppercase tracking-widest cursor-pointer" onClick={() => handleSort('username')}>
                                    <div className="flex items-center gap-1">User {sortConfig?.key === 'username' && (sortConfig.direction === 'asc' ? <ChevronUp size={10} /> : <ChevronDown size={10} />)}</div>
                                </th>
                                <th className="px-2 py-1.5 text-[10px] font-black text-slate-500 uppercase tracking-widest">Level</th>
                                <th className="px-2 py-1.5 text-[10px] font-black text-slate-500 uppercase tracking-widest">Auth</th>
                                <th className="px-2 py-1.5 text-[10px] font-black text-slate-500 uppercase tracking-widest">Created</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 font-sans">
                            {loading ? (
                                <tr><td colSpan={4} className="py-8 text-center"><RotateCcw className="w-4 h-4 text-[#422568] animate-spin mx-auto" /></td></tr>
                            ) : data.length === 0 ? (
                                <tr><td colSpan={4} className="py-8 text-center text-slate-400 text-[10px] font-bold uppercase tracking-widest">No users found.</td></tr>
                            ) : (
                                data.map((item) => (
                                    <tr key={item.id} className="hover:bg-slate-50/50 transition-colors">
                                        <td className="px-2 py-1.5 text-[11px] font-black text-slate-900">{item.username || '-'}</td>
                                        <td className="px-2 py-1.5">
                                            <span className="px-1.5 py-0.5 rounded text-[8px] font-black uppercase bg-blue-50 text-blue-600 border border-blue-100">
                                                {item.user_level || 'unknown'}
                                            </span>
                                        </td>
                                        <td className="px-2 py-1.5 text-[9px] font-black uppercase">
                                            {item.auth_success === null ? (
                                                <div className="flex items-center gap-1 text-slate-400"><Info size={10} /> Pending</div>
                                            ) : item.auth_success ? (
                                                <div className="flex items-center gap-1 text-emerald-600"><CheckCircle2 size={10} /> Success</div>
                                            ) : (
                                                <div className="flex items-center gap-1 text-rose-600"><XCircle size={10} /> Failed</div>
                                            )}
                                        </td>
                                        <td className="px-2 py-1.5 text-[10px] font-bold text-slate-500">{formatDate(item.created)}</td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                <div className="bg-slate-50 border-t border-slate-200 px-3 py-1.5">
                    <div className="flex justify-between items-center text-[9px]">
                        <span className="text-slate-400 font-black uppercase tracking-tighter">
                            {Math.min((currentPage - 1) * itemsPerPage + 1, totalItems)}-{Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems}
                        </span>
                        <div className="flex items-center gap-1">
                            <button onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))} disabled={currentPage === 1} className="p-1 text-slate-400 hover:text-slate-900 disabled:opacity-30 transition-colors">&lsaquo;</button>
                            <span className="bg-[#422568] text-white px-1.5 rounded font-black">{currentPage}</span>
                            <button onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))} disabled={currentPage === totalPages || totalPages === 0} className="p-1 text-slate-400 hover:text-slate-900 disabled:opacity-30 transition-colors">&rsaquo;</button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CameraUsers;
